import { AVAILABLE_LOCALES } from '@/i18n';
import { useT } from '@/hooks/useT';
import { cn } from '@/lib/cn';

interface LanguageToggleProps {
  variant?: 'pill' | 'inline';
  className?: string;
}

/**
 * Compact 2-way language toggle (pt-BR ↔ EN). Rendered as a segmented
 * control so both options are visible at once — cheaper cognition than
 * a dropdown and barely any real estate.
 */
export function LanguageToggle({ variant = 'pill', className }: LanguageToggleProps) {
  const { locale, setLocale } = useT();

  if (variant === 'inline') {
    return (
      <div
        role="radiogroup"
        aria-label="Language"
        className={cn(
          'inline-flex w-full items-stretch rounded-lg border border-line bg-surface-2 p-0.5 text-sm',
          className,
        )}
      >
        {AVAILABLE_LOCALES.map((loc) => {
          const active = loc.code === locale;
          return (
            <button
              key={loc.code}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setLocale(loc.code)}
              className={cn(
                'pressable flex-1 min-h-[44px] rounded-md px-3 py-1.5 text-center font-medium',
                active
                  ? 'bg-ink text-surface shadow-xs'
                  : 'text-ink-muted hover:text-ink',
              )}
            >
              <span className="mr-1.5" aria-hidden>
                {loc.flag}
              </span>
              {loc.label}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div
      role="radiogroup"
      aria-label="Language"
      className={cn(
        'inline-flex items-center rounded-full border border-line bg-surface-2 p-0.5 text-xs',
        className,
      )}
    >
      {AVAILABLE_LOCALES.map((loc) => {
        const active = loc.code === locale;
        return (
          <button
            key={loc.code}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => setLocale(loc.code)}
            className={cn(
              // Wave Onda4: ≥ 44pt min touch target on mobile; compact on sm+
              'pressable flex items-center gap-1 rounded-full px-3 py-2 font-semibold tracking-wide',
              'min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 sm:px-2.5 sm:py-1',
              active ? 'bg-ink text-surface' : 'text-ink-muted hover:text-ink',
            )}
          >
            <span aria-hidden>{loc.flag}</span>
            {loc.code === 'pt-BR' ? 'PT' : 'EN'}
          </button>
        );
      })}
    </div>
  );
}
